/**
 * 项目接口统一收口文件
 *
 *   · 所有后端接口只在这里定义一次，页面 / 组件统一从这里调用，不再散落 `${host_url}xxx`;
 *   · 请求走 src/utils/axios.js 的实例（自动带 authorization / Content-Type，统一超时与错误处理）;
 *   · 接口根地址来自环境变量 VUE_APP_API_BASE_URL（见根目录 .env 系列文件）。
 *
 * 用法：
 *   import { authApi, userApi, orderApi } from '@/api'
 *   const res = await userApi.getAccountInfo({ userId, orderId })
 *
 * 分组：
 *   authApi     登录 / 注册 / 验证码 / 找回密码 / 退出
 *   userApi     账号信息、企业认证
 *   portalApi   接口目录与接口详情（数据开放平台门户）
 *   orderApi    密钥、调用次数、调用统计、已申请接口
 *   payApi      充值、支付、发票、订单
 *   gatewayApi  在线测试的真实网关调用
 */
import http from '../utils/axios'
import { API_BASE_URL, GATEWAY_SCHEME } from '../config'

export { API_BASE_URL }

/**
 * 拼接完整请求地址。
 * 传入绝对地址（http/https 开头）时原样返回，其余按接口根地址补全。
 */
export function apiUrl(path) {
  if (!path) return path
  const target = String(path)
  if (/^https?:\/\//i.test(target)) return target
  return API_BASE_URL + target.replace(/^\/+/, '')
}

/** 对外暴露的接口清单（便于查阅与文档生成） */
export const endpoints = {
  auth: {
    sendCaptcha: 'user/captcha/get',
    login: 'user/password/login',
    loginByCaptcha: 'user/captcha/login',
    register: 'user/register',
    resetPassword: 'user/password/update',
    logout: 'user/logout'
  },
  user: {
    getAccountInfo: 'user/account/info',
    updatePhone: 'user/phone/update',
    updateUsername: 'user/username/update',
    updateEmail: 'user/email/update',
    getOrgBaseInfo: 'user/org/base/info',
    getOrgCertInfo: 'user/org/cert/info',
    getOrgCertState: 'user/org/cert/state',
    submitOrgCert: 'user/org/cert/submit',
    updateOrgCert: 'user/org/cert/update',
    imgUpload: 'user/img/upload'
  },
  portal: {
    getInterfaceList: 'portal/api/interface',
    getDimensionList: 'portal/api/interface/dim',
    getRecommendList: 'portal/api/interface/refer',
    getInterfaceCatalog: 'portal/api/dim/interface/detail',
    getInterfaceSamples: 'portal/api/dim/interface/home',
    getInterfaceDetail: 'portal/api/interface/details'
  },
  order: {
    getSecrets: 'order/secret',
    getLeftTimes: 'order/invoke/left/num',
    getInvokeStatistics: 'order/invoke/statistics',
    getInvokeStatisticsDetail: 'order/invoke/statistics/detail',
    getOrderInterfaces: 'order/interface/api',
    applyInterfaces: 'order/interface/api/apply',
    getAppliedInterfaces: 'order/interface/api/applied',
    deleteAppliedInterface: 'order/interface/api/applied/delete'
  },
  pay: {
    charge: 'pay/amount/charge',
    continueCharge: 'pay/amount/charge/continue',
    getChargeOrderState: 'pay/wx/charge/order/state',
    getChargeOrders: 'pay/charge/order',
    deleteChargeOrder: 'pay/charge/order/del',
    addInvoice: 'pay/charge/invoice/add',
    getInvoiceFixedInfo: 'pay/charge/order/invoice/fixed/info',
    getInvoiceDetail: 'pay/charge/order/invoice/detail'
  }
}

/** 登录 / 注册 / 验证码 / 退出 */
export const authApi = {
  /** 获取短信验证码 captchaType: 1 注册 2 登录 3 忘记密码 4 修改手机号 */
  sendCaptcha(params) {
    return http.get(apiUrl(endpoints.auth.sendCaptcha), params)
  },
  /** 账号密码登录 */
  login(params) {
    return http.post(apiUrl(endpoints.auth.login), params)
  },
  /** 短信验证码登录 */
  loginByCaptcha(params) {
    return http.post(apiUrl(endpoints.auth.loginByCaptcha), params)
  },
  /** 注册 */
  register(params) {
    return http.post(apiUrl(endpoints.auth.register), params)
  },
  /** 重置 / 修改密码 */
  resetPassword(params) {
    return http.post(apiUrl(endpoints.auth.resetPassword), params)
  },
  /** 退出登录 */
  logout(params) {
    return http.get(apiUrl(endpoints.auth.logout), params)
  }
}

/** 账号信息与企业认证 */
export const userApi = {
  /** 账号信息 */
  getAccountInfo(params) {
    return http.get(apiUrl(endpoints.user.getAccountInfo), params)
  },
  /** 修改手机号（需旧手机号与新手机号两个验证码） */
  updatePhone(params) {
    return http.post(apiUrl(endpoints.user.updatePhone), params)
  },
  /** 修改用户名 */
  updateUsername(params) {
    return http.post(apiUrl(endpoints.user.updateUsername), params)
  },
  /** 修改邮箱 */
  updateEmail(params) {
    return http.post(apiUrl(endpoints.user.updateEmail), params)
  },
  /** 根据企业名称查询工商信息 */
  getOrgBaseInfo(params) {
    return http.get(apiUrl(endpoints.user.getOrgBaseInfo), params)
  },
  /** 企业认证信息 */
  getOrgCertInfo(params) {
    return http.get(apiUrl(endpoints.user.getOrgCertInfo), params)
  },
  /** 企业认证状态 */
  getOrgCertState(params) {
    return http.get(apiUrl(endpoints.user.getOrgCertState), params)
  },
  /** 提交企业认证 */
  submitOrgCert(params) {
    return http.post(apiUrl(endpoints.user.submitOrgCert), params)
  },
  /** 更新企业认证信息 */
  updateOrgCert(params) {
    return http.post(apiUrl(endpoints.user.updateOrgCert), params)
  }
}

/** 图片上传地址（el-upload 的 action） */
export const imageUploadUrl = apiUrl(endpoints.user.imgUpload)

/** 接口目录与接口详情 */
export const portalApi = {
  /** 按分类 / 关键词分页查询接口 */
  getInterfaceList(params) {
    return http.get(apiUrl(endpoints.portal.getInterfaceList), params)
  },
  /** 接口分类清单 */
  getDimensionList(params) {
    return http.get(apiUrl(endpoints.portal.getDimensionList), params)
  },
  /** 高频 / 推荐接口 */
  getRecommendList(params) {
    return http.get(apiUrl(endpoints.portal.getRecommendList), params)
  },
  /** 分类 + 接口清单（左侧目录） */
  getInterfaceCatalog(params) {
    return http.get(apiUrl(endpoints.portal.getInterfaceCatalog), params)
  },
  /** 各分类下的示例接口 home: { dimIntfNum: 8 } */
  getInterfaceSamples(params) {
    return http.get(apiUrl(endpoints.portal.getInterfaceSamples), params)
  },
  /** 接口详情 id: { id } */
  getInterfaceDetail(params) {
    return http.get(apiUrl(endpoints.portal.getInterfaceDetail), params)
  }
}

/** 密钥、调用次数、调用统计、已申请接口 */
export const orderApi = {
  /** 按计费模式取密钥列表 { userId, billingMode } */
  getSecrets(params) {
    return http.get(apiUrl(endpoints.order.getSecrets), params)
  },
  /** 剩余调用次数 { orderId } */
  getLeftTimes(params) {
    return http.get(apiUrl(endpoints.order.getLeftTimes), params)
  },
  /** 调用统计 { orderId } */
  getInvokeStatistics(params) {
    return http.get(apiUrl(endpoints.order.getInvokeStatistics), params)
  },
  /** 调用统计明细 { orderId, intfName, month, pageNum } */
  getInvokeStatisticsDetail(params) {
    return http.get(apiUrl(endpoints.order.getInvokeStatisticsDetail), params)
  },
  /** 订单下可申请的接口 { userId, orderId, name, pageNum } */
  getOrderInterfaces(params) {
    return http.get(apiUrl(endpoints.order.getOrderInterfaces), params)
  },
  /** 申请接口 { orderId, intfIds } */
  applyInterfaces(params) {
    return http.post(apiUrl(endpoints.order.applyInterfaces), params)
  },
  /** 已申请接口 { orderId, name } */
  getAppliedInterfaces(params) {
    return http.get(apiUrl(endpoints.order.getAppliedInterfaces), params)
  },
  /** 删除已申请接口 { intfId, orderId } */
  deleteAppliedInterface(params) {
    return http.get(apiUrl(endpoints.order.deleteAppliedInterface), params)
  }
}

/** 充值 / 支付 / 发票 / 订单 */
export const payApi = {
  /** 发起充值 { userId, orderId, payType, chargeType, amount, giveAmount } */
  charge(params) {
    return http.post(apiUrl(endpoints.pay.charge), params)
  },
  /** 继续支付 { id } */
  continueCharge(params) {
    return http.get(apiUrl(endpoints.pay.continueCharge), params)
  },
  /** 轮询微信支付结果 { chargeOrderNo } */
  getChargeOrderState(params) {
    return http.get(apiUrl(endpoints.pay.getChargeOrderState), params)
  },
  /** 充值订单列表（查询条件由调用方过滤空值后传入） */
  getChargeOrders(params) {
    return http.post(apiUrl(endpoints.pay.getChargeOrders), params)
  },
  /** 删除充值订单 { userId, orderId } */
  deleteChargeOrder(params) {
    return http.get(apiUrl(endpoints.pay.deleteChargeOrder), params)
  },
  /** 新增 / 修改发票 */
  addInvoice(params) {
    return http.post(apiUrl(endpoints.pay.addInvoice), params)
  },
  /** 固定发票信息 { userId, orderId } */
  getInvoiceFixedInfo(params) {
    return http.get(apiUrl(endpoints.pay.getInvoiceFixedInfo), params)
  },
  /** 发票详情 { userId, chargeOrderId } */
  getInvoiceDetail(params) {
    return http.get(apiUrl(endpoints.pay.getInvoiceDetail), params)
  }
}

/** 在线接口测试：真实网关调用 */
export const gatewayApi = {
  /**
   * 调用真实接口
   * @param {string} domainUrl 接口域名（来自接口详情，可含协议）
   * @param {object} params    请求参数
   * @param {string} secretkey 密钥（放在请求头）
   */
  callApi(domainUrl, params, secretkey) {
    const target = String(domainUrl || '').replace(/^https?:\/\//i, '')
    return http.get(GATEWAY_SCHEME + '://' + target, params, { secretkey: secretkey })
  }
}

export default {
  apiUrl,
  endpoints,
  authApi,
  userApi,
  portalApi,
  orderApi,
  payApi,
  gatewayApi
}



// WEBPACK FOOTER //
// ./src/api/index.js